import React, { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { ChevronRight, Save, Send, Eye, Clock, FileCheck, Sparkles } from 'lucide-react';

// Hotspots = anchored points on the image with a pulsing dot + tooltip on hover.
// Each "x" and "y" is a percentage of the image container.
const HOTSPOTS_IT = [
  {
    id: 'save',
    x: 81, y: 4,
    icon: Save,
    title: 'Salva in 1 click',
    desc: 'Bozza automatica + invio definitivo. Nessun dato perso.',
    color: 'teal',
  },
  {
    id: 'timer',
    x: 41, y: 36,
    icon: Clock,
    title: 'Timer ciclo automatico',
    desc: '20 minuti precisi, conteggio in background, alert quando è ora.',
    color: 'orange',
  },
  {
    id: 'speculare',
    x: 60, y: 14,
    icon: Eye,
    title: 'OD e OS speculari',
    desc: 'Compili un occhio: l\'altro è già pronto. Zero copia-incolla.',
    color: 'teal',
  },
  {
    id: 'fse',
    x: 95, y: 4,
    icon: FileCheck,
    title: 'Concludi visita',
    desc: 'Genera referto, prepara documenti FSE 2.0, archivia. Tutto in 1 click.',
    color: 'orange',
  },
];

const HOTSPOTS_EN = [
  {
    id: 'save',
    x: 81, y: 4,
    icon: Save,
    title: 'Save in 1 click',
    desc: 'Auto-drafts + final submit. No data ever lost.',
    color: 'teal',
  },
  {
    id: 'timer',
    x: 41, y: 36,
    icon: Clock,
    title: 'Built-in cycle timer',
    desc: 'Precise 20-min countdown in background, alerts when ready.',
    color: 'orange',
  },
  {
    id: 'speculare',
    x: 60, y: 14,
    icon: Eye,
    title: 'OD ↔ OS mirrored',
    desc: 'Fill one eye, the other is pre-filled. Zero copy-paste.',
    color: 'teal',
  },
  {
    id: 'fse',
    x: 95, y: 4,
    icon: FileCheck,
    title: 'Conclude visit',
    desc: 'Generate report, prepare FSE 2.0 docs, archive. All in 1 click.',
    color: 'orange',
  },
];

const COLORS = {
  teal: {
    dot: 'bg-pv-teal',
    ring: 'ring-pv-teal/30',
    glow: 'bg-pv-teal/40',
    border: 'border-pv-teal/30',
    iconBg: 'bg-pv-ice',
    iconColor: 'text-pv-teal',
  },
  orange: {
    dot: 'bg-pv-orange',
    ring: 'ring-pv-orange/30',
    glow: 'bg-pv-orange/40',
    border: 'border-pv-orange/30',
    iconBg: 'bg-pv-peach',
    iconColor: 'text-pv-orange',
  },
};

const Hotspot = ({ spot, isActive, onEnter, onLeave }) => {
  const c = COLORS[spot.color];
  const Icon = spot.icon;
  const tooltipOnLeft = spot.x > 60;
  return (
    <button
      type="button"
      data-testid={`pv-hotspot-${spot.id}`}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onEnter}
      onBlur={onLeave}
      onClick={(e) => { e.preventDefault(); onEnter(); }}
      aria-label={spot.title}
      className="absolute z-20 group -translate-x-1/2 -translate-y-1/2"
      style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
    >
      {/* Pulse glow */}
      <span className={`absolute inset-0 rounded-full ${c.glow} blur-md opacity-70 animate-ping`} />
      {/* Dot */}
      <span className={`relative inline-flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full ${c.dot} ring-4 ${c.ring} shadow-[0_4px_12px_-2px_rgba(0,0,0,0.18)] transition-transform group-hover:scale-110`}>
        <Sparkles className="w-2.5 h-2.5 text-white" />
      </span>

      {/* Tooltip */}
      {isActive && (
        <div
          className={`absolute z-30 ${tooltipOnLeft ? 'right-full mr-3' : 'left-full ml-3'} top-1/2 -translate-y-1/2 w-60 bg-white rounded-xl border ${c.border} shadow-[0_20px_40px_-16px_rgba(15,23,42,0.25)] p-3.5 animate-pv-fade-up`}
          style={{ animationDuration: '0.25s' }}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
        >
          <div className="flex items-start gap-2.5 mb-1.5">
            <span className={`shrink-0 w-7 h-7 rounded-lg ${c.iconBg} ${c.border} border flex items-center justify-center`}>
              <Icon className={`w-3.5 h-3.5 ${c.iconColor}`} />
            </span>
            <div className="font-heading font-bold text-[13.5px] text-pv-ink leading-tight pt-0.5 text-left">
              {spot.title}
            </div>
          </div>
          <p className="text-[12px] text-slate-600 leading-snug text-left font-medium">
            {spot.desc}
          </p>
        </div>
      )}
    </button>
  );
};

export const DashboardMockup = () => {
  const { locale } = useLanguage();
  const [activeSpot, setActiveSpot] = useState(null);
  const spots = locale === 'en' ? HOTSPOTS_EN : HOTSPOTS_IT;

  return (
    <div className="relative w-full">
      <div className="relative pv-device-frame">
        {/* Window chrome — light */}
        <div className="pv-device-frame__chrome">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
          </div>
          <div className="hidden sm:flex items-center gap-2 text-[10.5px] font-mono uppercase tracking-[0.18em] text-slate-500 font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
            primevision.app · cartella clinica
          </div>
          <div className="text-[10.5px] font-mono uppercase tracking-[0.18em] text-slate-400 font-semibold">v 2.6</div>
        </div>

        {/* Real screenshot + hotspots overlay */}
        <div className="relative bg-white overflow-hidden">
          <img
            src="/assets/screens/cartella-clinica.png"
            alt="Cartella clinica Prime Vision — OD/OS, Visus, Autoref, Tonometria"
            className="w-full h-auto block select-none"
            style={{ aspectRatio: '1/1', objectFit: 'cover', objectPosition: 'top center' }}
            draggable={false}
          />

          {/* Hotspots layer */}
          <div className="absolute inset-0 pointer-events-none">
            {spots.map((spot) => (
              <div key={spot.id} className="pointer-events-auto">
                <Hotspot
                  spot={spot}
                  isActive={activeSpot === spot.id}
                  onEnter={() => setActiveSpot(spot.id)}
                  onLeave={() => setActiveSpot(null)}
                />
              </div>
            ))}
          </div>

          {/* Subtle bottom fade */}
          <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        </div>

        {/* Footer bar */}
        <div className="px-3 py-2.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-[10.5px] font-mono uppercase tracking-[0.18em] text-slate-500 font-semibold rounded-b-xl">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-pv-teal animate-pulse" />
            crittografato · GDPR
          </span>
          <span className="hidden sm:inline">auto-save · 10:15</span>
          <span className="flex items-center gap-1 text-pv-teal">live <ChevronRight className="w-2.5 h-2.5" /></span>
        </div>
      </div>
    </div>
  );
};

export default DashboardMockup;
